import { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { copyTextToClipboard } from "@/lib/clipboard";
import SettingsService from "services/Settings";

export function SwoogoWebhookCard({ hasToken }) {
  const queryClient = useQueryClient();
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);

  const createToken = useMutation({
    mutationFn: SettingsService.createSwoogoWebhookToken,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
    onError: error => toast.error(error.message),
  });
  const createdToken = createToken.data?.webhook_token;

  const handleCreate = () => {
    setIsReplaceDialogOpen(false);
    createToken.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Swoogo Webhook</CardTitle>
        <CardDescription>
          Swoogo webhook deliveries are accepted only when they include this
          token in the Authorization header.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-5">
        <Badge variant="secondary" data-testid="swoogo-webhook-token-status">
          {hasToken ? "Token configured" : "Token not configured"}
        </Badge>
        {createdToken && (
          <Alert className="tw:border-warning/50 tw:bg-warning/10 tw:text-warning">
            <TriangleAlert />
            <AlertTitle>Copy the token before leaving this page</AlertTitle>
            <AlertDescription className="tw:text-foreground">
              Add the token to the Swoogo webhook as a custom header named{" "}
              <code>Authorization</code>. It cannot be copied again after this
              page is closed or reloaded.
            </AlertDescription>
            <AlertAction>
              <Button
                type="button"
                size="xs"
                onClick={() => copyTextToClipboard(`Bearer ${createdToken}`)}
              >
                <Copy data-icon="inline-start" />
                Copy Token
              </Button>
            </AlertAction>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="tw:justify-end">
        <Button
          type="button"
          disabled={createToken.isPending}
          onClick={() =>
            hasToken ? setIsReplaceDialogOpen(true) : createToken.mutate()
          }
        >
          {hasToken ? "Replace Token" : "Create Token"}
        </Button>
      </CardFooter>
      <AlertDialog
        open={isReplaceDialogOpen}
        onOpenChange={setIsReplaceDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace webhook token?</AlertDialogTitle>
            <AlertDialogDescription>
              The current token will be revoked immediately. Swoogo webhook
              deliveries will be rejected until the new token is configured in
              Swoogo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreate}>
              Replace Token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

SwoogoWebhookCard.propTypes = {
  hasToken: PropTypes.bool.isRequired,
};
